'use client'

import { useEffect, useRef, useState } from 'react'

import { XMarkIcon } from '@heroicons/react/24/outline'

import { Button } from '~/components/component-library'
import { useIsStandalone } from '~/hooks/useIsStandalone'

const DISMISS_KEY = 'splits.pwaInstallBannerDismissed'

export function getPlatform(): 'ios' | 'android' | null {
  if (typeof navigator === 'undefined') return null
  const ua = navigator.userAgent
  if (/iPhone|iPad|iPod/.test(ua)) return 'ios'
  if (/Android/.test(ua)) return 'android'
  return null
}

export const PWAInstallBanner = () => {
  const isStandalone = useIsStandalone()
  const [platform, setPlatform] = useState<'ios' | 'android' | null>(null)
  const [show, setShow] = useState(false)
  const [mounted, setMounted] = useState(false)
  const innerRef = useRef<HTMLDivElement>(null)
  const outerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (isStandalone) return
    const dismissed = localStorage.getItem(DISMISS_KEY)
    if (dismissed) {
      setMounted(false)
      return
    }
    const p = getPlatform()
    if (!p) return
    setPlatform(p)
    setMounted(true)
    setShow(true)
  }, [isStandalone])

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, '1')
    setShow(false)
  }

  const handleTransitionEnd = () => {
    if (!show) setMounted(false)
  }

  if (!mounted || isStandalone) return null

  return (
    <div
      ref={outerRef}
      className="grid overflow-hidden transition-[grid-template-rows] duration-300 ease-in-out"
      style={{ gridTemplateRows: show ? '1fr' : '0fr' }}
      onTransitionEnd={handleTransitionEnd}
    >
      <div ref={innerRef} className="min-h-0">
        <div className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-600 dark:bg-gray-950 dark:text-gray-300">
          <div className="min-w-0 flex-1">
            {platform === 'ios' ? (
              <span>
                Install Splits: tap the <strong>Share</strong> button,
                <br /> then click <strong>Add to home screen</strong>.
              </span>
            ) : (
              <span>
                Install Splits: tap the <strong>menu ⋮</strong> button,
                <br /> then click <strong>Add to home screen</strong>.
              </span>
            )}
          </div>
          <div className="align-self-center">
            <Button
              onClick={dismiss}
              variant="tertiary"
              size="small"
              prefix={<XMarkIcon className="h-4 w-4" />}
              aria-label="Dismiss"
            ></Button>
          </div>
        </div>
      </div>
    </div>
  )
}
